import React, { Fragment, useEffect, useState } from 'react';
import { SdpResponse, headPointPhysicalMeter } from '../../../../models/SdpResponse';
import { useGetBudgetHistory, useGetConsumptionHistory } from '../../../../hooks/budgets';

import { ReactComponent as BarChartIcon } from '../../../../assets/chart-bar.svg';
import { BudgetResponse } from '../../../../models/BudgetResponse';
import { DateTime } from 'luxon';
import EmptyState from '../../../common/EmptyState';
import ForecastHistoryTooltip from './ForecastHistoryTooltip';
import { ReactComponent as GraphSkeletonLoader } from '../../../../assets/graph-skeleton-loader.svg';
import HgChart from '../../../common/HgChart';
import LastUpdateData from '../../../common/LastUpdateData';
import { renderToString } from 'react-dom/server';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { userPreferenceUnitState } from '../../../../atoms/userPreferencesAtom/userPreferencesAtom';

export interface MeterForecastHistoryWidgetProps {
  sdp?: SdpResponse | null;
}
export default function MeterForecastHistoryWidget({ sdp }: MeterForecastHistoryWidgetProps) {
  const { i18n, t } = useTranslation();
  const unit = useRecoilValue(userPreferenceUnitState);
  const {
    data: budgetHistory,
    isFetching: isFetchingBudgetHistory,
    isError: isErrorBudgetHistory,
  } = useGetBudgetHistory(sdp?.id, DateTime.now().year, unit);

  const {
    data: consumptionsHistory,
    isFetching: isFetchingConsumptionsHistory,
    isError: isErrorConsumptionsHistory,
  } = useGetConsumptionHistory(sdp?.id, DateTime.now().year, unit);

  const [selectedChartPoint, setSelectedChartPoint] = useState<{
    category: string;
    budget: BudgetResponse;
  } | null>();

  const [chartData, setChartData] = useState({
    chart: {
      backgroundColor: null,
      scrollablePlotArea: {
        minWidth: 400,
        scrollPositionX: 0.5,
      },
      panning: {
        enabled: true,
        type: 'x',
      },
    },
    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },
    legend: {
      useHTML: true,
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      className: 'highcharts-legend',
      shared: true,
      backgroundColor: 'none',
    },
    scrollbar: {
      enabled: true,
    },
    yAxis: {
      title: {
        text: null,
      },
    },
    xAxis: {
      crosshair: {
        color: '#ada9a7',
        zIndex: 3,
        width: 4,
      },
      labels: {
        formatter: function (context: any) {
          return DateTime.fromISO(context.value, {
            locale: i18n.language,
          }).toFormat('MMMMM');
        },
      },
    },

    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            mouseOver: function () {
              const self = this as any;
              setSelectedChartPoint({
                category: self.category,
                budget: {
                  currentExpenses: self.series.chart.series[1].data[self.x].y,
                  targetAmount: self.series.chart.series[0].data[self.x].y,
                  startDate: self.category,
                } as BudgetResponse,
              });
            },
          },
        },
      },
    },
    series: [
      {
        type: 'line',
        color: '#f72020',
        plotOptions: {
          column: {
            shadow: false,
            stacking: 'normal',
          },
          series: {
            marker: {
              enabled: false,
            },
            legendSymbol: 'lineMarker',
            lineWidth: 15,
          },
        },
        marker: {
          fillColor: '#FFFFFF',
          radius: 5,
          lineWidth: 2,
          lineColor: null, // inherit from series
        },
        zIndex: 2,
      },
      {
        type: 'column',
        stacking: 'normal',
        color: '#a3117b',
        zIndex: 1,
      },
    ],
  });

  useEffect(() => {
    const budgets = [...(budgetHistory?.data || []), ...(consumptionsHistory?.data || [])];
    if (
      budgets &&
      budgets.length > 0 &&
      isFetchingBudgetHistory === false &&
      isFetchingConsumptionsHistory === false &&
      isErrorBudgetHistory === false &&
      isErrorConsumptionsHistory === false
    ) {
      setChartData((prevState) => {
        const categories = budgets.map((item) =>
          DateTime.fromISO(item.startDate, {
            locale: i18n.language,
          })
            .toJSDate()
            .toISOString()
        );

        const budgetSeries = budgetHistory?.data.map((item) => ({
          y: item.targetAmount,
        }));

        const consumptionSeries = consumptionsHistory?.data.map((item) => ({
          y: item.currentExpenses,
        }));

        return {
          ...prevState,
          xAxis: {
            ...prevState.xAxis,
            categories,
          },
          tooltip: {
            ...prevState.tooltip,
            formatter: function () {
              return renderToString(
                <ForecastHistoryTooltip
                  className="highcharts-tooltip__content--large"
                  hasInfo={false}
                  title={DateTime.fromISO(selectedChartPoint?.budget.startDate!, {
                    locale: i18n.language,
                  }).toFormat('MMM yyyy')}
                  unit={unit}
                  consumptionValue={selectedChartPoint?.budget.currentExpenses}
                  previsionValue={selectedChartPoint?.budget.targetAmount}
                />
              );
            },
          },
          series: [
            {
              ...prevState.series[0],
              data: budgetSeries,
            },
            {
              ...prevState.series[1],
              data: consumptionSeries,
            },
          ],
        };
      });
    }
  }, [
    budgetHistory?.data,
    consumptionsHistory?.data,
    i18n.language,
    isErrorBudgetHistory,
    isErrorConsumptionsHistory,
    isFetchingBudgetHistory,
    isFetchingConsumptionsHistory,
    selectedChartPoint?.budget.currentExpenses,
    selectedChartPoint?.budget.startDate,
    selectedChartPoint?.budget.targetAmount,
    unit,
  ]);

  return (
    <div className="meter-forecast-history-widget">
      {isFetchingBudgetHistory || isErrorConsumptionsHistory ? (
        <GraphSkeletonLoader height={300} className="chart__skeleton" />
      ) : budgetHistory?.data.length === 0 || consumptionsHistory?.data.length === 0 ? (
        <EmptyState message={t('common.no.data')} icon={<BarChartIcon />} />
      ) : (
        <Fragment>
          <div className="meter-forecast-history-widget__card">
            <ForecastHistoryTooltip
              className="highcharts-tooltip__content--small"
              title={DateTime.fromISO(selectedChartPoint?.budget.startDate!, {
                locale: i18n.language,
              }).toFormat('MMMM yyyy')}
              unit={unit}
              consumptionValue={selectedChartPoint?.budget.currentExpenses}
              previsionValue={selectedChartPoint?.budget.targetAmount}
            />
          </div>
          <HgChart chartData={chartData} />
          {headPointPhysicalMeter(sdp?.headpoint)?.lastAvailableDay && (
            <LastUpdateData
              date={new Date(headPointPhysicalMeter(sdp?.headpoint)?.lastAvailableDay!)}
            />
          )}
        </Fragment>
      )}
    </div>
  );
}
